import { useState } from 'react';

import { useTranslation } from '@kgc/i18n';

import { cn } from '@/lib/utils';

/** Local-time YYYY-MM-DD (avoids the UTC off-by-one of `toISOString`). */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as a local-midnight Date (null if invalid). */
export function parseISODate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface DayCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

/** 6×7 month grid, Monday-first, including leading/trailing days of adjacent months. */
export function buildMonthGrid(year: number, month0: number): DayCell[] {
  const first = new Date(year, month0, 1);
  // JS getDay(): 0=Sun..6=Sat → Monday-first offset
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month0, 1 - offset);
  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ iso: toISODate(d), day: d.getDate(), inMonth: d.getMonth() === month0 });
  }
  return cells;
}

interface ApplyResult {
  start: string;
  end: string;
  nextField: 'start' | 'end';
}

/** Pure selection logic — keeps start ≤ end and advances start → end for a smooth pick. */
export function applyDaySelection(
  field: 'start' | 'end',
  iso: string,
  start: string,
  end: string
): ApplyResult {
  if (field === 'start') {
    const nextEnd = end && end >= iso ? end : iso;
    return { start: iso, end: nextEnd, nextField: 'end' };
  }
  // end field
  if (start && iso < start) {
    // clicked before start → treat as a new start
    return { start: iso, end, nextField: 'end' };
  }
  return { start, end: iso, nextField: 'end' };
}

const HU = 'hu-HU'; // matches the codebase's hardcoded date locale (lib/utils)
const monthFmt = new Intl.DateTimeFormat(HU, { year: 'numeric', month: 'long' });
const WEEKDAYS = (() => {
  const f = new Intl.DateTimeFormat(HU, { weekday: 'short' });
  // 2024-01-01 is a Monday → Monday-first labels
  return Array.from({ length: 7 }, (_, i) => f.format(new Date(2024, 0, 1 + i)));
})();

interface RentalDateRangeCalendarProps {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  minDate?: string;
}

/**
 * rental/03 (A): custom inline date-range calendar replacing the native
 * `<input type="date">` pair. FE-only — emits the same YYYY-MM-DD strings the
 * wizard store already consumes. The per-machine discount (B) is Phase-2.
 */
export function RentalDateRangeCalendar({
  startDate,
  endDate,
  onChange,
  minDate,
}: RentalDateRangeCalendarProps) {
  const { t } = useTranslation();
  const [activeField, setActiveField] = useState<'start' | 'end'>('start');
  const [view, setView] = useState(() => {
    const base = parseISODate(startDate) ?? new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const grid = buildMonthGrid(view.year, view.month);
  const monthLabel = monthFmt.format(new Date(view.year, view.month, 1));

  const goMonth = (delta: number) => {
    setView(v => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const handleDayClick = (iso: string) => {
    const res = applyDaySelection(activeField, iso, startDate, endDate);
    onChange(res.start, res.end);
    setActiveField(res.nextField);
  };

  const isDisabled = (iso: string) => Boolean(minDate) && iso < (minDate as string);
  const inRange = (iso: string) => startDate && endDate && iso > startDate && iso < endDate;

  const fieldBtn = (field: 'start' | 'end', label: string, value: string) => (
    <button
      type="button"
      data-testid={`calendar-field-${field}`}
      data-active={activeField === field}
      onClick={() => setActiveField(field)}
      className={cn(
        'flex-1 rounded-lg border-2 p-3 text-left transition',
        activeField === field
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-gray-200 dark:border-[#2d7d8a]'
      )}
    >
      <span className="block text-xs text-gray-500 dark:text-[#ccfbf1]">{label}</span>
      <span className="block font-semibold text-gray-900 dark:text-white">{value || '—'}</span>
    </button>
  );

  return (
    <div data-testid="rental-date-range-calendar" className="space-y-3">
      <div className="flex gap-3">
        {fieldBtn('start', t('rental.startDate'), startDate)}
        {fieldBtn('end', t('rental.wizard.returnDateLabel'), endDate)}
      </div>

      <div className="rounded-lg border border-gray-200 p-3 dark:border-[#2d7d8a]">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            aria-label={t('rental.wizard.calendar.prevMonth')}
            onClick={() => goMonth(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-[#0f3d47]"
          >
            ‹
          </button>
          <span data-testid="calendar-month-label" className="text-sm font-semibold">
            {monthLabel}
          </span>
          <button
            type="button"
            aria-label={t('rental.wizard.calendar.nextMonth')}
            onClick={() => goMonth(1)}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-[#0f3d47]"
          >
            ›
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-400">
          {WEEKDAYS.map(w => (
            <span key={w} className="py-1">
              {w}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map(cell => {
            const disabled = isDisabled(cell.iso);
            const isStart = cell.iso === startDate;
            const isEnd = cell.iso === endDate;
            const selected = isStart || isEnd;
            return (
              <button
                key={cell.iso}
                type="button"
                disabled={disabled}
                data-testid={`calendar-day-${cell.iso}`}
                data-selected={selected || undefined}
                onClick={() => handleDayClick(cell.iso)}
                className={cn(
                  'flex h-9 items-center justify-center rounded-md text-sm transition',
                  !cell.inMonth && 'text-gray-300 dark:text-gray-600',
                  disabled && 'cursor-not-allowed opacity-30',
                  !disabled && !selected && 'hover:bg-primary/10',
                  inRange(cell.iso) && !selected && 'bg-primary/10',
                  selected && 'bg-primary font-semibold text-white'
                )}
              >
                {cell.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
